import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Shield,
  Globe2,
  Wallet,
  Building2,
  Cpu,
  ArrowRight,
  Database,
  Lock,
  Zap,
  CheckCircle2,
  MessageSquare,
  UploadCloud,
  ChevronDown,
  ChevronUp,
  Users,
  Headset,
  Scale,
  Server,
  Clock,
  Sparkles,
  HardDrive,
  EyeOff,
  BadgeCheck,
  FolderOpen,
  MessageCircle,
  Settings,
  FileText,
  TrendingUp,
  Target,
} from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import {
  buildOrganizationJsonLd,
  buildSoftwareJsonLd,
  buildWebSiteJsonLd,
  buildFAQPageJsonLd,
  buildBreadcrumbListJsonLd,
  buildSiteNavigationElementJsonLd,
} from "../../seo/site";

const clients = [
  {
    name: "Centrimmo",
    url: "https://www.centrimmo.fr/",
    logo: "https://img.netty.fr/logo/centr/2/logo.png?c=1773328035",
  },
  {
    name: "R2D-Automation",
    url: "https://www.r2d-automation.com/",
    logo: "https://www.r2d-automation.com/wp-content/uploads/2022/11/R2D_Logo_60X22.png",
  },
];

const SEO_DESCRIPTION =
  "Raguia : assistant documentaire IA B2B souverain — chatbot RAG, portail client, OCR, hébergement OVH France, IA Mistral européenne. Solution clé en main sécurisée, démarche RGPD.";

const FAQ_ITEMS = [
  {
    q: "Mes documents confidentiels restent-ils en France ?",
    a: "Oui, vos documents sont hébergés en France chez OVH. Nous utilisons des modèles d'IA qui ne s'entraînent pas sur vos données. Vos documents restent strictement les vôtres.",
  },
  {
    q: "Que se passe-t-il si je veux arrêter l'abonnement ?",
    a: "Vous pouvez arrêter à tout moment. Nous supprimons toutes vos données, documents et index lors de la résiliation.",
  },
  {
    q: "Faut-il savoir coder pour utiliser Raguia ?",
    a: "Non. Le portail est aussi simple qu'un drive. Vous glissez vos documents, et c'est prêt.",
  },
  {
    q: "Peut-on séparer les documents par équipe ?",
    a: "Oui. Le service RH ne voit que ses documents, la comptabilité ne voit que les siens, etc.",
  },
  {
    q: "L'IA peut-elle inventer des réponses ?",
    a: "Non. Raguia lit vos documents et cite la source. Si la réponse n'est pas dans vos documents, l'IA le dit. Nous recommandons toujours de vérifier les informations importantes.",
  },
];

export default function HomePage() {
  const [activeTab, setActiveTab] = useState("portail");

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildWebSiteJsonLd(),
    buildSoftwareJsonLd(),
    buildFAQPageJsonLd(FAQ_ITEMS),
    buildBreadcrumbListJsonLd([{ name: "Accueil", path: "/" }]),
    ...buildSiteNavigationElementJsonLd(),
  ];

  return (
    <div className="bg-stone-50 font-body text-[#010101] selection:bg-[#A43032] selection:text-white min-h-screen">
      <SeoHead
        title="Raguia — Assistant IA B2B Souverain | Sécurisé France | RAG"
        description={SEO_DESCRIPTION}
        jsonLd={jsonLd}
      />

      {/* HERO SECTION - Avec éléments visuels attractifs */}
      <section className="relative px-4 pt-16 pb-12 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col items-center text-center overflow-hidden">
        {/* Éléments décoratifs */}
        <div className="absolute top-20 left-10 size-72 bg-[#A43032]/5 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 size-96 bg-[#A43032]/10 rounded-full blur-3xl" />

        {/* Badge animé */}
        <div className="relative inline-flex items-center gap-2 rounded-full border-2 border-[#A43032]/30 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-8 backdrop-blur-sm">
          <span className="relative flex size-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A43032] opacity-75"></span>
            <span className="relative inline-flex rounded-full size-2.5 bg-[#A43032]"></span>
          </span>
          <span>🇫🇷</span> Vos documents restent en France
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-[#010101] max-w-5xl leading-[1.1] relative z-10">
          Posez VOS questions à VOS documents avec{" "}
          <span className="text-[#A43032] relative inline-block">
            Raguia
            <svg
              className="absolute w-full h-4 -bottom-4 left-0 text-[#A43032]"
              viewBox="0 0 100 10"
              preserveAspectRatio="none"
            >
              <path
                d="M0 5 Q 50 10 100 5"
                stroke="currentColor"
                strokeWidth="5"
                fill="transparent"
              />
            </svg>
          </span>
        </h1>
        <p className="sr-only">
          Solution RAG IA B2B pour PME françaises — chatbot documentaire RGPD
          hébergé en France. Assistant IA sur vos documents internes : PDF,
          Word, Excel. Conforme RGPD, hébergé chez OVH en France.
        </p>

        <p className="mt-8 text-lg md:text-xl text-stone-600 max-w-2xl font-medium relative z-10">
          Un chatbot qui lit vos documents PDF, Word, Excel et répond à vos
          questions. Hébergé en France, vos données restent confidentielles.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-md relative z-10">
          <Link
            to="/contact"
            className="w-full sm:w-auto inline-flex flex-col justify-center items-center rounded-2xl bg-gradient-to-r from-[#A43032] to-[#8a2729] px-8 py-4 text-base font-bold text-white shadow-2xl shadow-[#A43032]/30 transition-all hover:scale-105 hover:bg-[#8a2729] hover:shadow-[#A43032]/50"
          >
            <span className="flex items-center gap-2">
              Essayer gratuitement <ArrowRight className="size-5" />
            </span>
            <span className="text-xs font-normal opacity-90 mt-1">
              Réponse sous 24h sur vos documents
            </span>
          </Link>
          <Link
            to="/tarifs"
            className="w-full sm:w-auto inline-flex justify-center items-center rounded-2xl border-2 border-stone-200 bg-white px-8 py-4 text-base font-bold text-[#010101] transition-all hover:border-[#A43032] hover:text-[#A43032] shadow-lg hover:shadow-xl"
          >
            Voir les prix
          </Link>
        </div>

        {/* Trust badges avec icônes */}
        <div className="flex flex-wrap gap-3 items-center justify-center mt-12 max-w-4xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700 shadow-sm hover:shadow-md hover:border-[#A43032]/30 transition-all">
            <span>🇫🇷</span>
            <span>Hébergé en France</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700 shadow-sm hover:shadow-md hover:border-[#A43032]/30 transition-all">
            <Lock className="size-4 text-[#A43032]" />
            <span>Sécurité assurée</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700 shadow-sm hover:shadow-md hover:border-[#A43032]/30 transition-all">
            <EyeOff className="size-4 text-[#A43032]" />
            <span>Données privées</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700 shadow-sm hover:shadow-md hover:border-[#A43032]/30 transition-all">
            <BadgeCheck className="size-4 text-[#A43032]" />
            <span>Conforme RGPD</span>
          </span>
        </div>
      </section>

      {/* 3 AVANTAGES CLÉS - Plus compact */}
      <section className="px-4 py-8 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="grid grid-cols-3 gap-3 md:gap-6">
          {/* Carte 1 - compacte */}
          <div className="group bg-gradient-to-br from-[#A43032] to-[#8a2729] rounded-[1.5rem] md:rounded-[2rem] p-4 md:p-6 text-white text-center shadow-lg shadow-[#A43032]/20 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
            <div className="size-10 md:h-12 md:w-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-2 md:mb-3">
              <Clock className="size-5 md:h-6 md:w-6 text-white" />
            </div>
            <p className="text-2xl md:text-4xl font-black mb-1">65 min</p>
            <p className="text-white/90 font-medium text-xs md:text-base">
              gagnées/jour
            </p>
          </div>

          {/* Carte 2 - compacte */}
          <div className="group bg-[#010101] rounded-[1.5rem] md:rounded-[2rem] p-4 md:p-6 text-white text-center shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 size-16 bg-[#A43032]/20 rounded-full blur-xl" />
            <div className="size-10 md:h-12 md:w-12 rounded-xl bg-[#A43032]/30 flex items-center justify-center mx-auto mb-2 md:mb-3 relative z-10">
              <Server className="size-5 md:h-6 md:w-6 text-[#A43032]" />
            </div>
            <p className="text-2xl md:text-4xl font-black text-[#A43032] mb-1 relative z-10">
              100%
            </p>
            <p className="text-stone-300 font-medium text-xs md:text-base relative z-10">
              en France
            </p>
          </div>

          {/* Carte 3 - compacte */}
          <div className="group bg-white rounded-[1.5rem] md:rounded-[2rem] p-4 md:p-6 text-center border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300">
            <div className="size-10 md:h-12 md:w-12 rounded-xl bg-[#A43032]/10 flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-[#A43032] group-hover:text-white transition-colors">
              <Zap className="size-5 md:h-6 md:w-6 text-[#A43032] group-hover:text-white" />
            </div>
            <p className="text-2xl md:text-4xl font-black text-[#A43032] mb-1">
              24h
            </p>
            <p className="text-stone-600 font-medium text-xs md:text-base">
              mise en place
            </p>
          </div>
        </div>
      </section>

      {/* À QUOI ÇA SERT - Comparatif visuel */}
      <section className="px-4 py-12 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-8 text-center">
          Vos collaborateurs perdent du temps à chercher des informations ?
        </h2>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Avant - style "problème" */}
          <div className="bg-gradient-to-br from-stone-100 to-stone-200 rounded-[2rem] p-6 md:p-8 border-2 border-stone-300 relative overflow-hidden">
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="size-12 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
                  <Target className="size-6" />
                </div>
                <p className="font-bold text-[#010101] text-lg">Sans Raguia</p>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-stone-600">
                  <span className="text-red-500 font-bold text-xl shrink-0">
                    ✗
                  </span>
                  <span>Recherche dans des dossiers imbriqués</span>
                </li>
                <li className="flex items-start gap-3 text-stone-600">
                  <span className="text-red-500 font-bold text-xl shrink-0">
                    ✗
                  </span>
                  <span>Demander à un collègue qui ne répond pas</span>
                </li>
                <li className="flex items-start gap-3 text-stone-600">
                  <span className="text-red-500 font-bold text-xl shrink-0">
                    ✗
                  </span>
                  <span>Ouvrir 10 documents pour trouver une info</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Après - style "solution" */}
          <div className="group relative bg-gradient-to-br from-[#010101] to-stone-900 rounded-[2rem] p-1">
            <div className="absolute inset-0 bg-[#A43032]/20 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-gradient-to-br from-[#010101] to-stone-900 rounded-[calc(2rem-4px)] p-6 md:p-8 text-white h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="size-12 rounded-xl bg-[#A43032]/30 flex items-center justify-center">
                  <TrendingUp className="size-6 text-[#A43032]" />
                </div>
                <p className="font-bold text-lg text-[#A43032]">Avec Raguia</p>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-white/90">
                  <CheckCircle2 className="size-6 text-[#A43032] shrink-0" />
                  <span>Posez votre question en français</span>
                </li>
                <li className="flex items-start gap-3 text-white/90">
                  <CheckCircle2 className="size-6 text-[#A43032] shrink-0" />
                  <span>La réponse est instantanée</span>
                </li>
                <li className="flex items-start gap-3 text-white/90">
                  <CheckCircle2 className="size-6 text-[#A43032] shrink-0" />
                  <span>La source est indiquée (document, page)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA FONCTIONNE - Étapes visuelles */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#010101] mb-4">
            3 étapes, zéro compétence technique
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto font-medium">
            Nous nous occupons de tout. Vous n'avez plus qu'à utiliser.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 relative">
          {/* Ligne de connexion */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-1 bg-gradient-to-r from-[#A43032]/20 via-[#A43032] to-[#A43032]/20 rounded-full" />

          <div className="bg-white rounded-[2rem] p-6 md:p-8 border-2 border-stone-200 shadow-lg text-center relative hover:border-[#A43032]/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <div className="size-20 rounded-full bg-gradient-to-br from-[#A43032] to-[#8a2729] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#A43032]/30 group-hover:scale-110 transition-transform">
              <FolderOpen className="size-10 text-white" />
            </div>
            <div className="size-8 rounded-full bg-[#010101] text-white flex items-center justify-center font-bold mx-auto mb-4 relative z-10 border-4 border-white shadow-lg">
              1
            </div>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-[#010101] mb-3">
              Envoyez vos documents
            </h3>
            <p className="text-stone-600 text-sm md:text-base font-medium leading-relaxed">
              Glissez vos PDF, Word, Excel dans le portail. C'est aussi simple
              qu'un drive.
            </p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 md:p-8 border-2 border-stone-200 shadow-lg text-center relative hover:border-[#A43032]/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <div className="size-20 rounded-full bg-gradient-to-br from-[#A43032] to-[#8a2729] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#A43032]/30 group-hover:scale-110 transition-transform">
              <Settings className="size-10 text-white" />
            </div>
            <div className="size-8 rounded-full bg-[#010101] text-white flex items-center justify-center font-bold mx-auto mb-4 relative z-10 border-4 border-white shadow-lg">
              2
            </div>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-[#010101] mb-3">
              Nous configurons tout
            </h3>
            <p className="text-stone-600 text-sm md:text-base font-medium leading-relaxed">
              En 24h, votre chatbot est prêt. Nous l'adaptons à votre métier et
              à vos couleurs.
            </p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 md:p-8 border-2 border-stone-200 shadow-lg text-center relative hover:border-[#A43032]/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <div className="size-20 rounded-full bg-gradient-to-br from-[#A43032] to-[#8a2729] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#A43032]/30 group-hover:scale-110 transition-transform">
              <MessageCircle className="size-10 text-white" />
            </div>
            <div className="size-8 rounded-full bg-[#010101] text-white flex items-center justify-center font-bold mx-auto mb-4 relative z-10 border-4 border-white shadow-lg">
              3
            </div>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-[#010101] mb-3">
              Posez vos questions
            </h3>
            <p className="text-stone-600 text-sm md:text-base font-medium leading-relaxed">
              "Quelle est notre politique de remboursement ?" → Réponse +
              document source.
            </p>
          </div>
        </div>
      </section>

      {/* SÉCURITÉ ET SOUVERAINETÉ - Vrai Bento Grid 2x2 parfait */}
      <section className="px-4 py-12 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-8 text-center">
          Vos données sont protégées
        </h2>

        {/* Grille 2 colonnes égales - parfaitement alignée */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* LIGNE 1 - Gauche: Sécurité (haute), Droite: France */}
          {/* 1. Sécurité - 2 lignes de hauteur */}
          <div className="md:row-span-2 rounded-[1.5rem] bg-[#010101] p-5 md:p-6 text-white relative overflow-hidden group border border-stone-800 shadow-xl hover:-translate-y-1 hover:shadow-2xl hover:border-stone-600 transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-[#A43032]/20 to-transparent opacity-50" />
            <div className="absolute top-0 right-0 size-40 bg-[#A43032]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-10 rounded-xl bg-[#A43032]/20 flex items-center justify-center border border-[#A43032]/40 shrink-0">
                  <Shield className="size-5 text-[#A43032]" />
                </div>
                <span className="px-2 py-1 rounded-full bg-[#A43032]/20 text-[#A43032] text-xs font-bold border border-[#A43032]/30">
                  Sécurité maximale
                </span>
              </div>

              <h3 className="font-heading text-lg font-semibold mb-3">
                Vos documents sont en sécurité
              </h3>

              <div className="space-y-2">
                <div className="flex items-start gap-2 p-2 rounded-lg bg-white/5">
                  <CheckCircle2 className="size-4 text-[#A43032] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-white text-sm">
                      Isolation complète
                    </p>
                    <p className="text-xs text-stone-400">
                      Cloisonnement strict par département
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 rounded-lg bg-white/5">
                  <CheckCircle2 className="size-4 text-[#A43032] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-white text-sm">
                      Chiffrement total
                    </p>
                    <p className="text-xs text-stone-400">
                      Vos fichiers sont chiffrés
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 rounded-lg bg-white/5">
                  <CheckCircle2 className="size-4 text-[#A43032] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-white text-sm">
                      Pas d'entraînement
                    </p>
                    <p className="text-xs text-stone-400">
                      Vos documents ne servent pas l'IA
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-stone-400">
                <HardDrive className="size-4 text-[#A43032]" />
                <span>Sauvegarde quotidienne</span>
              </div>
            </div>
          </div>

          {/* 2. France - Ligne 1, Droite */}
          <div className="rounded-[1.5rem] bg-white p-5 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-lg bg-[#A43032]/10 flex items-center justify-center text-[#A43032] group-hover:bg-[#A43032] group-hover:text-white transition-colors shrink-0">
                <Globe2 className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading text-base font-semibold text-[#010101]">
                    En France
                  </h3>
                  <span className="text-lg">🇫🇷</span>
                </div>
                <p className="text-stone-600 text-sm leading-snug mb-3">
                  Toutes vos données sont stockées et traitées sur le territoire
                  français, chez OVH Cloud.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs font-semibold text-[#A43032] bg-[#A43032]/10 px-2 py-1 rounded-full">
                    OVH Cloud
                  </span>
                  <span className="text-xs font-semibold text-[#A43032] bg-[#A43032]/10 px-2 py-1 rounded-full">
                    RGPD
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* LIGNE 2 - Droite: IA */}
          {/* 3. IA Française - Ligne 2, Droite */}
          <div className="rounded-[1.5rem] bg-white p-5 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-lg bg-[#010101] flex items-center justify-center text-white group-hover:bg-[#A43032] transition-colors shrink-0">
                <Sparkles className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading text-base font-semibold text-[#010101]">
                    IA française
                  </h3>
                  <span className="text-lg">🇪🇺</span>
                </div>
                <p className="text-stone-600 text-sm leading-snug mb-3">
                  Nous utilisons les modèles de Mistral AI, une société
                  française. Les traitements sont effectués depuis l'Union
                  européenne et respectent la réglementation européenne sur
                  l'intelligence artificielle, sans dépendance aux États-Unis.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs font-semibold text-[#A43032] bg-[#A43032]/10 px-2 py-1 rounded-full font-mono">
                    Mistral AI
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* LIGNE 3 - Pleine largeur: Clé en main */}
          {/* 4. Clé en main - Rouge, 2 colonnes */}
          <div className="md:col-span-2 rounded-[1.5rem] bg-gradient-to-r from-[#A43032] to-[#8a2729] p-6 text-white relative overflow-hidden shadow-lg shadow-[#A43032]/20 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group">
            <div className="absolute right-0 bottom-0 opacity-20 translate-x-6 translate-y-6">
              <Zap className="size-20" />
            </div>
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="size-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                <Zap className="size-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-base font-semibold mb-1">
                  Clé en main — Votre solution en 48 heures
                </h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  Nous configurons tout pour vous : création de votre espace,
                  paramétrage du chatbot pour votre métier. Vous n'avez aucune
                  infrastructure à gérer, nous gérons les mises à jour et la
                  sécurité.
                </p>
              </div>
            </div>
          </div>

          {/* LIGNE 4 - Gauche: Prix, Droite: Portail */}
          {/* 5. Prix */}
          <div className="rounded-[1.5rem] bg-white p-5 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-lg bg-stone-100 flex items-center justify-center text-[#A43032] group-hover:bg-[#A43032]/10 transition-colors shrink-0">
                <Wallet className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-stone-500 mb-1">À partir de</p>
                <p className="text-2xl font-black text-[#010101] group-hover:text-[#A43032] transition-colors mb-2">
                  90
                  <span className="text-base text-stone-400 font-bold font-mono">
                    €/mois
                  </span>
                </p>
                <p className="text-sm text-stone-600 leading-snug mb-2">
                  Prix fixe et prévisible. Pas de surprise selon votre nombre de
                  documents ou d'utilisateurs.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="size-3 text-[#A43032]" /> Sans
                    engagement
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="size-3 text-[#A43032]" /> Mises à
                    jour incluses
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="size-3 text-[#A43032]" />{" "}
                    Engagement 12 mois + Parrainage cummulable
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 6. Portail personnalisé */}
          <div className="rounded-[1.5rem] bg-white p-5 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-lg bg-white flex items-center justify-center shadow-sm text-[#A43032] shrink-0">
                <MessageSquare className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-base font-semibold text-[#010101] mb-2">
                  Espace personnalisé
                </h3>
                <p className="text-sm text-stone-600 leading-snug mb-3">
                  Un portail marque blanche avec vos couleurs et votre logo. Vos
                  collaborateurs accèdent à un espace qui vous ressemble.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs font-semibold text-[#A43032] bg-white px-2 py-1 rounded-full border border-stone-200">
                    Marque blanche
                  </span>
                  <span className="text-xs font-semibold text-[#A43032] bg-white px-2 py-1 rounded-full border border-stone-200">
                    Votre logo
                  </span>
                  <span className="text-xs font-semibold text-[#A43032] bg-white px-2 py-1 rounded-full border border-stone-200">
                    Vos couleurs
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RAG EXPLICATION - Section plus visuelle */}
      <section className="px-4 py-12 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="rounded-[2rem] bg-[#010101] p-6 md:p-8 lg:p-12 text-white relative overflow-hidden border border-stone-800 shadow-2xl">
          <div className="absolute top-0 right-0 -mt-32 -mr-32 size-96 bg-[#A43032]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 -mb-32 -ml-32 size-64 bg-[#A43032]/10 rounded-full blur-3xl" />

          <div className="relative z-10 grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#A43032]/50 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-6 backdrop-blur-sm">
                <Database className="size-4" />
                Comment l'IA trouve les réponses
              </div>

              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold mb-4 md:mb-6 leading-tight">
                Des réponses basées sur{" "}
                <span className="text-[#A43032]">VOS documents</span>
              </h2>

              <p className="text-base md:text-lg text-stone-400 font-medium mb-6 md:mb-8 leading-relaxed">
                Contrairement à ChatGPT, Raguia lit vos fichiers et cite ses
                sources.
              </p>

              <div className="space-y-3">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="size-10 rounded-full bg-[#A43032] flex items-center justify-center shrink-0 shadow-lg shadow-[#A43032]/30">
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      Vous posez une question
                    </p>
                    <p className="text-sm text-stone-400">
                      "Quelle est notre politique de congés ?"
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="size-10 rounded-full bg-[#A43032] flex items-center justify-center shrink-0 shadow-lg shadow-[#A43032]/30">
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      L'IA cherche dans vos documents
                    </p>
                    <p className="text-sm text-stone-400">
                      Elle lit les PDF, Word, Excel que vous avez uploadés
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#A43032]/10 border border-[#A43032]/30">
                  <div className="size-10 rounded-full bg-[#A43032] flex items-center justify-center shrink-0 shadow-lg shadow-[#A43032]/30">
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      Réponse + Source exacte
                    </p>
                    <p className="text-sm text-stone-400">
                      &ldquo;25 jours de congés — Source :{" "}
                      <span className="font-mono font-bold">
                        guide_salarie.pdf
                      </span>{" "}
                      page 12&rdquo;
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mock Chat amélioré */}
            <div className="rounded-2xl bg-stone-900/50 backdrop-blur-sm p-5 border border-stone-700 shadow-2xl">
              <div className="flex items-center gap-3 pb-4 border-b border-stone-700 mb-4">
                <div className="size-10 rounded-full bg-gradient-to-br from-[#A43032] to-[#8a2729] flex items-center justify-center shadow-lg">
                  <Database className="size-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white">Raguia Assistant</p>
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs text-stone-500">En ligne</span>
                  </div>
                </div>
                <div className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold border border-emerald-500/30">
                  Actif
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-full bg-stone-700 flex items-center justify-center shrink-0 text-sm font-semibold text-stone-300">
                    V
                  </div>
                  <div className="rounded-xl bg-stone-800 px-4 py-2.5 text-sm text-stone-200 max-w-[85%]">
                    Quelle est notre politique de remboursement des frais ?
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-full bg-gradient-to-br from-[#A43032] to-[#8a2729] flex items-center justify-center shrink-0">
                    <Database className="size-4 text-white" />
                  </div>
                  <div className="rounded-xl bg-[#A43032]/10 px-4 py-3 text-sm border border-[#A43032]/30 max-w-[90%]">
                    <p className="mb-2 text-stone-200">
                      D'après{" "}
                      <span className="text-[#A43032] font-semibold bg-[#A43032]/20 px-2 py-0.5 rounded font-mono">
                        note_service.pdf
                      </span>{" "}
                      :
                    </p>
                    <p className="text-stone-100 leading-relaxed">
                      Les frais professionnels sont remboursés sur présentation
                      d'un justificatif, dans la limite de 50€ par mois.
                    </p>
                    <div className="mt-3 flex items-center gap-2 text-xs text-[#A43032]">
                      <FileText className="size-3" />
                      <span>Source : Page 5, paragraphe 2</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-stone-700 flex gap-2 flex-wrap">
                <span className="px-3 py-1.5 rounded-lg bg-stone-800 text-xs text-stone-400 border border-stone-700">
                  Lit vos PDF
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-stone-800 text-xs text-stone-400 border border-stone-700">
                  Lit vos Word
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-[#A43032]/20 text-[#A43032] text-xs font-bold border border-[#A43032]/30">
                  Cite les sources
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERFACE PRODUIT */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#010101] mb-4">
            Une interface simple
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Pas de formation nécessaire. Si vous utilisez un drive, vous saurez
            utiliser Raguia.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="bg-stone-100 p-1.5 rounded-xl flex gap-1 shadow-inner">
            <Button
              variant={activeTab === "portail" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("portail")}
            >
              Votre portail documents
            </Button>
            <Button
              variant={activeTab === "chatbot" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("chatbot")}
            >
              Le chatbot
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-stone-50 shadow-2xl max-w-5xl mx-auto overflow-hidden">
          {activeTab === "portail" ? (
            <div className="w-full bg-stone-200 flex items-center justify-center relative overflow-hidden rounded-2xl aspect-[16/9] md:aspect-auto">
              <span className="text-stone-700 font-medium absolute z-10 p-2 bg-white/50 backdrop-blur-sm rounded">
                Portail client avec documents listés
              </span>
              <img
                src="/interface_portail.webp"
                alt="Interface du Portail Administrateur"
                className="w-full h-auto relative z-20"
                style={{ objectFit: "contain" }}
                loading="lazy"
                decoding="async"
                width="1200"
                height="800"
              />
            </div>
          ) : (
            <div className="w-full bg-stone-200 flex items-center justify-center relative overflow-hidden rounded-2xl aspect-[16/9] md:aspect-auto">
              <span className="text-stone-700 font-medium absolute z-10 p-2 bg-white/50 backdrop-blur-sm rounded">
                Chatbot avec question/réponse sourcée
              </span>
              <img
                src="/interface_chatbot.webp"
                alt="Interface du chatbot IA"
                className="w-full h-auto relative z-20"
                style={{ objectFit: "contain" }}
                loading="lazy"
                decoding="async"
                width="1200"
                height="800"
              />
            </div>
          )}
        </div>
      </section>

      {/* POUR QUI */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200 bg-[#fcfafb]">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#010101] mb-4">
            Qui utilise Raguia ?
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto font-medium">
            Toute entreprise qui a beaucoup de documents à gérer
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Link
            to="/secteurs/rh"
            className="bg-white rounded-[1.5rem] p-6 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group block"
          >
            <div className="size-12 md:h-14 md:w-14 rounded-2xl bg-gradient-to-br from-[#A43032]/20 to-[#A43032]/10 flex items-center justify-center mb-5 group-hover:bg-[#A43032] group-hover:text-white transition-colors text-[#A43032]">
              <Users className="size-6 md:h-7 md:w-7" />
            </div>
            <h3 className="font-heading font-semibold text-[#010101] mb-2 text-base md:text-lg group-hover:text-[#A43032] transition-colors">
              Cabinets RH
            </h3>
            <p className="text-sm text-stone-600 font-medium leading-relaxed">
              Règlements intérieurs, fiches de poste, conventions collectives —
              vos consultants trouvent les réponses instantanément.
            </p>
            <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#A43032] opacity-0 group-hover:opacity-100 transition-opacity">
              Découvrir <ArrowRight className="size-3" />
            </span>
          </Link>

          <Link
            to="/secteurs/immobilier"
            className="bg-white rounded-[1.5rem] p-6 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group block"
          >
            <div className="size-12 md:h-14 md:w-14 rounded-2xl bg-gradient-to-br from-[#A43032]/20 to-[#A43032]/10 flex items-center justify-center mb-5 group-hover:bg-[#A43032] group-hover:text-white transition-colors text-[#A43032]">
              <Building2 className="size-6 md:h-7 md:w-7" />
            </div>
            <h3 className="font-heading font-semibold text-[#010101] mb-2 text-base md:text-lg group-hover:text-[#A43032] transition-colors">
              Immobilier
            </h3>
            <p className="text-sm text-stone-600 font-medium leading-relaxed">
              Mandats, compromis, diagnostics, baux — retrouvez instantanément
              les infos sur chaque bien ou client.
            </p>
            <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#A43032] opacity-0 group-hover:opacity-100 transition-opacity">
              Découvrir <ArrowRight className="size-3" />
            </span>
          </Link>

          <Link
            to="/secteurs/avocats"
            className="bg-white rounded-[1.5rem] p-6 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group block"
          >
            <div className="size-12 md:h-14 md:w-14 rounded-2xl bg-gradient-to-br from-[#A43032]/20 to-[#A43032]/10 flex items-center justify-center mb-5 group-hover:bg-[#A43032] group-hover:text-white transition-colors text-[#A43032]">
              <Scale className="size-6 md:h-7 md:w-7" />
            </div>
            <h3 className="font-heading font-semibold text-[#010101] mb-2 text-base md:text-lg group-hover:text-[#A43032] transition-colors">
              Avocats & Juristes
            </h3>
            <p className="text-sm text-stone-600 font-medium leading-relaxed">
              Conclusions, contrats, dossiers complexes — analysez en secondes
              avec un secret professionnel absolu.
            </p>
            <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#A43032] opacity-0 group-hover:opacity-100 transition-opacity">
              Découvrir <ArrowRight className="size-3" />
            </span>
          </Link>

          <Link
            to="/secteurs/comptables"
            className="bg-white rounded-[1.5rem] p-6 border-2 border-stone-200 shadow-lg hover:-translate-y-1 hover:shadow-xl hover:border-[#A43032]/30 transition-all duration-300 group block"
          >
            <div className="size-12 md:h-14 md:w-14 rounded-2xl bg-gradient-to-br from-[#A43032]/20 to-[#A43032]/10 flex items-center justify-center mb-5 group-hover:bg-[#A43032] group-hover:text-white transition-colors text-[#A43032]">
              <Headset className="size-6 md:h-7 md:w-7" />
            </div>
            <h3 className="font-heading font-semibold text-[#010101] mb-2 text-base md:text-lg group-hover:text-[#A43032] transition-colors">
              Experts-Comptables
            </h3>
            <p className="text-sm text-stone-600 font-medium leading-relaxed">
              Bilans, liasses fiscales, dossiers clients — retrouvez n'importe
              quelle donnée en quelques secondes.
            </p>
            <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#A43032] opacity-0 group-hover:opacity-100 transition-opacity">
              Découvrir <ArrowRight className="size-3" />
            </span>
          </Link>
        </div>

        {/* Autres secteurs */}
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <p className="w-full text-center text-sm font-semibold text-stone-400 uppercase tracking-widest mb-1">
            Et aussi
          </p>
          <Link
            to="/secteurs/notaires"
            className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-stone-200 px-5 py-2.5 text-sm font-bold text-stone-700 hover:border-[#A43032] hover:text-[#A43032] transition-all shadow-sm"
          >
            Notaires <ArrowRight className="size-3.5" />
          </Link>
          <Link
            to="/secteurs/recrutement"
            className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-stone-200 px-5 py-2.5 text-sm font-bold text-stone-700 hover:border-[#A43032] hover:text-[#A43032] transition-all shadow-sm"
          >
            Cabinets de recrutement <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="py-12 border-y border-stone-200 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-stone-400">
            Ils gagnent du temps avec Raguia
          </p>
        </div>
        <div className="relative flex overflow-hidden group justify-center">
          <div
            className={
              clients.length >= 5
                ? "animate-marquee whitespace-nowrap flex items-center gap-16 group-hover:[animation-play-state:paused]"
                : "flex flex-wrap items-center justify-center gap-8 md:gap-16 w-full px-4"
            }
          >
            {(clients.length >= 5
              ? [...clients, ...clients, ...clients, ...clients]
              : clients
            ).map((client, idx) => (
              <a
                key={idx}
                href={client.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-3 transition-transform hover:scale-105 mx-4"
                title={`Visiter le site de ${client.name}`}
              >
                {client.logo ? (
                  <>
                    <img
                      src={client.logo}
                      alt={`Logo ${client.name}`}
                      className="h-10 md:h-12 w-auto object-contain grayscale opacity-60 transition-all duration-300 group-hover:grayscale-0 group-hover:opacity-100"
                    />
                    <span className="text-xs font-semibold text-stone-500 group-hover:text-[#A43032] transition-colors">
                      {client.name}
                    </span>
                  </>
                ) : (
                  <span className="text-2xl font-black text-stone-300 transition-colors group-hover:text-[#A43032]">
                    {client.name}
                  </span>
                )}
              </a>
            ))}
          </div>
          {clients.length >= 5 && (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-white to-transparent"></div>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-white to-transparent"></div>
            </>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#010101] mb-4">
            Questions fréquentes
          </h2>
          <p className="text-stone-600 font-medium">
            Ce que vous devez savoir avant de commencer
          </p>
        </div>
        <div className="space-y-4">
          {FAQ_ITEMS.map((faq) => (
            <details
              key={faq.q}
              className="group bg-white rounded-2xl border-2 border-stone-200 overflow-hidden [&_summary::-webkit-details-marker]:hidden shadow-lg hover:shadow-xl hover:border-[#A43032]/20 transition-all"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-1.5 p-6 text-[#010101] font-bold outline-none hover:bg-stone-50 transition-colors">
                {faq.q}
                <span className="relative ml-1.5 size-5 shrink-0">
                  <ChevronDown className="absolute inset-0 size-5 opacity-100 group-open:opacity-0 transition-opacity" />
                  <ChevronUp className="absolute inset-0 size-5 opacity-0 group-open:opacity-100 transition-opacity" />
                </span>
              </summary>
              <div className="px-6 pb-6 text-stone-600 font-medium leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-4 py-20 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="rounded-[2.5rem] bg-[#010101] p-8 md:p-16 text-center relative overflow-hidden border border-stone-800 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-[#A43032]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 size-64 md:w-96 md:h-96 bg-[#A43032]/20 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
          <div className="absolute top-0 right-0 size-48 bg-[#A43032]/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />

          <div className="relative z-10 flex flex-col items-center">
            <div className="size-16 rounded-2xl bg-gradient-to-br from-[#A43032] to-[#8a2729] flex items-center justify-center mb-6 shadow-lg shadow-[#A43032]/30">
              <Cpu className="size-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
              Arrêtez de chercher dans vos dossiers, on le fait pour vous.
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-stone-300 max-w-2xl mb-10 font-medium">
              Demandez une démonstration gratuite sur vos propres documents.
              Vous verrez le temps gagné immédiatement.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Link
                to="/contact"
                className="inline-flex justify-center items-center gap-2 rounded-2xl bg-gradient-to-r from-[#A43032] to-[#8a2729] px-8 py-4 text-base font-bold text-white transition-all hover:scale-105 shadow-xl shadow-[#A43032]/30 hover:shadow-[#A43032]/50"
              >
                Tester sur vos documents <ArrowRight className="size-5" />
              </Link>
              <Link
                to="/tarifs"
                className="inline-flex justify-center items-center gap-2 rounded-2xl border-2 border-stone-600 px-8 py-4 text-base font-bold text-white transition-all hover:border-[#A43032] hover:text-[#A43032]"
              >
                Voir les prix
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-stone-400">
                <CheckCircle2 className="size-5 text-[#A43032]" />
                <span className="font-semibold">Démo gratuite</span>
              </div>
              <div className="flex items-center gap-2 text-stone-400">
                <CheckCircle2 className="size-5 text-[#A43032]" />
                <span className="font-semibold">Sans engagement</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
